import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Map, Plus, Trash2, Loader2, MapPin } from "lucide-react";

const VERTICALS = ["solar", "mining", "media"];
const STATUSES = ["operational", "construction", "planned"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ name: "", vertical: "solar", location: "", lat: "", lng: "", capacity: "", status: "operational", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/projects").then(({ data }) => setProjects(data)).catch((e) => console.error(e)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) });
      toast.success("Project added");
      setForm({ name: "", vertical: "solar", location: "", lat: "", lng: "", capacity: "", status: "operational", description: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { try { await api.delete(`/projects/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B3B39]">Projects &amp; GIS Editor</h1><p className="text-slate-600 mt-1">Manage geo-located infrastructure assets.</p></div>

      <form onSubmit={add} className="oj-card p-6 mb-8" data-testid="project-form">
        <h3 className="oj-h3 mb-5 flex items-center gap-2"><Plus className="w-5 h-5 text-teal-600" /> Add Project</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <input required placeholder="Project Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="oj-input" data-testid="project-name" />
          <input required placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="oj-input" data-testid="project-location" />
          <input placeholder="Capacity (e.g. 450 MW)" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="oj-input" data-testid="project-capacity" />
          <input required type="number" step="any" placeholder="Latitude" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="oj-input" data-testid="project-lat" />
          <input required type="number" step="any" placeholder="Longitude" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="oj-input" data-testid="project-lng" />
          <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className="oj-input capitalize" data-testid="project-vertical">{VERTICALS.map((v) => <option key={v} className="bg-teal-950">{v}</option>)}</select>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="oj-input capitalize" data-testid="project-status">{STATUSES.map((s) => <option key={s} className="bg-teal-950">{s}</option>)}</select>
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="oj-input resize-none sm:col-span-2 lg:col-span-1" rows={1} data-testid="project-description" />
        </div>
        <button disabled={saving} className="oj-btn-gold mt-5" data-testid="project-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Project</button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {projects.map((p) => (
          <div key={p.id} className="oj-card p-6 group" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1"><span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-teal-50 text-teal-700 border border-teal-200 capitalize">{p.vertical}</span><span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase capitalize ${p.status === "operational" ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{p.status}</span></div>
                <h3 className="font-bold text-white">{p.name}</h3>
                <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-teal-400" /> {p.location} • {p.lat?.toFixed?.(4)}, {p.lng?.toFixed?.(4)}</div>
                {p.capacity && <div className="text-sm gold-text font-mono font-bold mt-1">{p.capacity}</div>}
              </div>
              <button onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-red-600/20 text-red-300 hover:bg-red-600/40 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
            {p.description && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
